import React from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Briefcase, Activity } from "lucide-react";

const STATUS_META = {
  online: { label: "Online", dot: "bg-success" },
  on_call: { label: "On Call", dot: "bg-info" },
  on_shift: { label: "On Shift", dot: "bg-primary" },
  idle: { label: "Idle", dot: "bg-warning" },
  away: { label: "Away", dot: "bg-escalation" },
  wrap_up: { label: "Wrap-up", dot: "bg-automation" },
  break: { label: "Break", dot: "bg-muted-foreground" },
  meeting: { label: "Meeting", dot: "bg-info" },
  training: { label: "Training", dot: "bg-primary" },
  dnd: { label: "Do Not Disturb", dot: "bg-critical" },
  offline: { label: "Offline", dot: "bg-muted-foreground" },
};
const OPTIONS = ["online", "on_shift", "on_call", "idle", "away", "wrap_up", "break", "meeting", "training", "dnd", "offline"];

export default function Team() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({ queryKey: ["team-live"], queryFn: async () => (await api.get("/team/live")).data, refetchInterval: 15000 });

  const statusMut = useMutation({
    mutationFn: ({ id, live_status }) => api.patch(`/users/${id}/status`, { live_status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["team-live"] }); qc.invalidateQueries({ queryKey: ["war-room"] }); toast.success("Status updated"); },
  });

  if (isLoading || !data) return <div><PageHeader title="Team Live" /><Loading /></div>;
  const members = data.members;
  const me = members.find((m) => m.id === user?.id);

  return (
    <div>
      <PageHeader title="Team Live" subtitle="Real-time workforce visibility" testid="team-header">
        <div className="flex items-center gap-2">
          <Label className="text-xs text-muted-foreground">My status</Label>
          <Select value={me?.live_status || "online"} onValueChange={(v) => statusMut.mutate({ id: user.id, live_status: v })}>
            <SelectTrigger className="w-40" data-testid="my-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>{OPTIONS.map((o) => <SelectItem key={o} value={o}>{STATUS_META[o].label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </PageHeader>

      <div className="mb-5 flex flex-wrap gap-2">
        {Object.entries(data.summary).map(([s, c]) => (
          <div key={s} className="flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1.5 text-sm" data-testid={`summary-${s}`}>
            <span className={`h-2 w-2 rounded-full ${STATUS_META[s]?.dot || "bg-muted-foreground"}`} />
            {STATUS_META[s]?.label || s} <span className="font-semibold">{c}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {members.map((m) => {
          const meta = STATUS_META[m.live_status] || STATUS_META.offline;
          return (
            <Card key={m.id} className="p-4" data-testid={`member-${m.id}`}>
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Avatar className="h-11 w-11"><AvatarFallback className="bg-primary/10 font-semibold text-primary">{m.name.split(" ").map((x) => x[0]).slice(0, 2).join("")}</AvatarFallback></Avatar>
                  <span className={`absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full border-2 border-card ${meta.dot}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{m.name}</p>
                  <p className="truncate text-xs capitalize text-muted-foreground">{m.role?.replace(/_/g, " ")}</p>
                </div>
                <span className="text-xs font-medium">{meta.label}</span>
              </div>
              <div className="mt-3 flex items-center justify-between border-t border-border pt-3 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><Briefcase className="h-3.5 w-3.5" /> {m.team || "—"}</span>
                <span className="flex items-center gap-1"><Activity className="h-3.5 w-3.5" /> Load {m.workload ?? 0}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
